/**
 * Utilidades para mostrar imágenes alojadas en Google Drive.
 *
 * Los links que Drive comparte (".../file/d/ID/view?usp=sharing") no se pueden
 * usar directamente en una etiqueta <img>. Se convierten al formato de
 * miniatura pública, que sí se puede embeber:
 *   https://drive.google.com/thumbnail?id=ID_DEL_ARCHIVO&sz=w1200
 */

/** Placeholder elegante de VCARS cuando una imagen no carga o falta. */
export const VCARS_PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600">
      <defs>
        <radialGradient id="g" cx="50%" cy="45%" r="65%">
          <stop offset="0%" stop-color="#12203a"/>
          <stop offset="100%" stop-color="#080b12"/>
        </radialGradient>
      </defs>
      <rect width="800" height="600" fill="url(#g)"/>
      <rect x="0" y="0" width="800" height="600" fill="none" stroke="#1f7bff" stroke-opacity="0.25" stroke-width="2"/>
      <text x="400" y="300" text-anchor="middle" font-family="Inter, Arial, sans-serif"
        font-size="64" font-weight="800" letter-spacing="14" fill="#4d9bff">VCARS</text>
      <text x="400" y="345" text-anchor="middle" font-family="Inter, Arial, sans-serif"
        font-size="20" fill="#8b94a7">Foto en preparación</text>
    </svg>`,
  )

const DRIVE_ID_PATTERNS = [
  /\/file\/d\/([a-zA-Z0-9_-]{10,})/,
  /[?&]id=([a-zA-Z0-9_-]{10,})/,
  /\/d\/([a-zA-Z0-9_-]{10,})/,
]

/** Extrae el ID del archivo desde cualquier formato de link de Drive. */
export function driveFileId(url: string): string | null {
  for (const pattern of DRIVE_ID_PATTERNS) {
    const match = pattern.exec(url)
    if (match) return match[1]
  }
  return null
}

/**
 * Devuelve una URL que se puede usar en <img>.
 * - Links de Drive → miniatura pública de 1200px.
 * - Cualquier otra URL (CDN, imagen directa) → se devuelve tal cual.
 * - Vacío o inválido → placeholder de VCARS.
 */
export function imageUrl(raw: string | undefined | null, width = 1200): string {
  const value = (raw ?? '').trim()
  if (!value) return VCARS_PLACEHOLDER

  if (value.includes('drive.google.com') || value.includes('docs.google.com')) {
    const id = driveFileId(value)
    if (id) return `https://drive.google.com/thumbnail?id=${id}&sz=w${width}`
  }

  if (/^https?:\/\//i.test(value) || value.startsWith('data:')) return value
  return VCARS_PLACEHOLDER
}

/** Handler para <img onError>: cae al placeholder de VCARS una sola vez. */
export function onImageError(
  event: React.SyntheticEvent<HTMLImageElement>,
): void {
  const img = event.currentTarget
  if (img.dataset.fallbackApplied === 'true') return
  img.dataset.fallbackApplied = 'true'
  img.src = VCARS_PLACEHOLDER
}
